import { useState, useEffect } from 'react';

const KEY = 'jp.tier4.drive.autoware.experimental';
const STANDBY_HEARTBEAT_INTERVAL_MS = 500;
const STANDBY_HEALTHY_TIMEOUT_MS = 3000;

export type ExtraConfigRaw = {
    subChannels: number;
    showAllStreams: boolean;
    heartbeatFrequency: number;
    heartbeatTimeout: number;
    streamSelector: 'v1' | 'v2';
    debugLog: boolean;
    devastatorMode: boolean;
}

export type ExtraConfig = Partial<ExtraConfigRaw>;

function useExtraConfigEntry<T extends keyof ExtraConfig>(key: T, extra: ExtraConfig, defaultValue: ExtraConfigRaw[T], setExtra: React.Dispatch<React.SetStateAction<ExtraConfig>>) {
    const [value, set] = useState<ExtraConfigRaw[T]>(() => extra[key] ?? defaultValue);

    useEffect(() => {
        set(extra[key] ?? defaultValue);
    }, [defaultValue, extra, key]);

    useEffect(() => {
        setExtra(extra => {
            if (extra[key] === value) {
                return extra;
            } else {
                const tmp = { ...extra };
                tmp[key] = value;
                return tmp;
            }
        });
    }, [key, setExtra, value]);

    return value;
}

export function useHeartbeatFrequency(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('heartbeatFrequency', e, 1000 / STANDBY_HEARTBEAT_INTERVAL_MS, s); }
export function useHeartbeatTimeout(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('heartbeatTimeout', e, STANDBY_HEALTHY_TIMEOUT_MS, s); }
export function useSubChannels(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('subChannels', e, 0, s); }
export function useShowAllStreams(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('showAllStreams', e, false, s); }
export function useStreamSelector(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('streamSelector', e, 'v1', s); }
export function useDebugLog(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('debugLog', e, false, s); }
export function useDevastatorMode(e: ExtraConfig, s: React.Dispatch<React.SetStateAction<ExtraConfig>>) { return useExtraConfigEntry('devastatorMode', e, false, s); }

export function useExtraFromSessionStorage() {
    const [extra, setExtra] = useState<ExtraConfig>(() => JSON.parse(window.sessionStorage.getItem(KEY) ?? '{}'));
    useEffect(() => {
        sessionStorage.setItem(KEY, JSON.stringify(extra));
    }, [extra]);

    return { extra, setExtra };
}
